import { useEffect, useState, useCallback } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  TextField, Box, Button, Grid, Alert, Typography, Chip, IconButton
} from '@mui/material';
import { Delete, Link as LinkIcon } from '@mui/icons-material';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import DropZone from '../common/DropZone';
import { uploadSingleImage, uploadDocument } from '../../services/upload.service';

const documentSchema = z.object({
  title: z.string().min(1, 'Укажите название'),
  category: z.string().optional(),
  description: z.string().optional(),
  externalLink: z.string().url('Некорректный URL').optional().or(z.literal('')),
});

type DocumentFormValues = z.infer<typeof documentSchema>;

type UploadedFileType = 'pdf' | 'image' | 'link';

interface UploadedFile {
  url: string;
  type: UploadedFileType;
  name?: string;
}

// GET /documents returns attachments as DocumentFile rows
// ({ id, documentId, fileUrl, fileType, ... }), while the API expects
// { url, type, name } on save — echoing the rows back failed validation.
function toUploadedFiles(files: unknown): UploadedFile[] {
  if (!Array.isArray(files)) return [];
  return files
    .map((file): UploadedFile | null => {
      const row = file as { url?: string; type?: string; fileUrl?: string; fileType?: string; name?: string };
      const url = row.url ?? row.fileUrl;
      if (!url) return null;
      const rawType = row.type ?? row.fileType;
      const type: UploadedFileType =
        rawType === 'pdf' || rawType === 'image' || rawType === 'link' ? rawType : 'image';
      return { url, type, name: row.name };
    })
    .filter((file): file is UploadedFile => file !== null);
}

interface DocumentsFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: DocumentFormValues & { files: UploadedFile[] }) => Promise<void>;
  initialData?: DocumentFormValues & { files?: UploadedFile[] } | null;
  loading: boolean;
  error?: string | null;
}

export function DocumentsForm({ open, onClose, onSubmit, initialData, loading, error }: DocumentsFormProps) {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<DocumentFormValues>({
    resolver: zodResolver(documentSchema),
    defaultValues: {
      title: '',
      category: '',
      description: '',
      externalLink: '',
    },
  });

  const externalLink = watch('externalLink');

  useEffect(() => {
    if (initialData) {
      reset({
        title: initialData.title || '',
        category: initialData.category || '',
        description: initialData.description || '',
        externalLink: initialData.externalLink || '',
      });
      setFiles(toUploadedFiles(initialData.files));
    } else {
      reset({
        title: '',
        category: '',
        description: '',
        externalLink: '',
      });
      setFiles([]);
    }
  }, [initialData, open, reset]);

  // Handle file upload via DropZone
  const handleFileUpload = useCallback(async (uploadedFiles: File[]): Promise<{ url: string }[]> => {
    setUploading(true);
    try {
      const results: { url: string }[] = [];
      for (const file of uploadedFiles) {
        // Use uploadDocument for PDF files, uploadSingleImage for images
        const url = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
          ? await uploadDocument(file)
          : await uploadSingleImage(file);
        const fileType = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf') ? 'pdf' : 'image';
        results.push({ url });
        setFiles(prev => [...prev, { url, type: fileType, name: file.name }]);
      }
      return results;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка загрузки';
      throw new Error(message);
    } finally {
      setUploading(false);
    }
  }, []);

  const handleFileChange = useCallback((urls: string | string[]) => {
    // DropZone returns URLs after upload
    const urlArray = Array.isArray(urls) ? urls : [urls];
    urlArray.forEach(url => {
      // Determine type from URL extension
      const fileType = url.toLowerCase().endsWith('.pdf') ? 'pdf' : 'image';
      if (!files.find(f => f.url === url)) {
        setFiles(prev => [...prev, { url, type: fileType }]);
      }
    });
  }, [files]);

  const handleRemoveFile = useCallback((index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleFormSubmit = useCallback(async (data: DocumentFormValues) => {
    await onSubmit({ ...data, files });
  }, [files, onSubmit]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {initialData ? 'Редактировать документ' : 'Добавить документ'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            {/* Title */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Название *"
                error={!!errors.title}
                helperText={errors.title?.message}
                {...register('title')}
              />
            </Grid>
            
            {/* Category */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Категория"
                placeholder="Например: Указы, Приказы, Распоряжения"
                {...register('category')}
              />
            </Grid>
            
            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Описание"
                multiline
                rows={3}
                placeholder="Краткое описание документа"
                {...register('description')}
              />
            </Grid>
            
            {/* File Upload */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Загрузить PDF файл
              </Typography>
              <DropZone
                onUpload={handleFileUpload}
                type="document"
                accept={{
                  'application/pdf': ['.pdf'],
                }}
                maxFiles={1}
                maxSize={50}
                multiple={false}
                onChange={handleFileChange}
                label="Перетащите PDF файл или нажмите для выбора"
                helperText="PDF до 50MB"
              />
              {uploading && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Загрузка файлов...
                </Typography>
              )}
              
              {/* Show uploaded files */}
              {files.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {files.map((file, index) => (
                    <Chip
                      key={index}
                      label={file.name || `Файл ${index + 1}`}
                      icon={file.type === 'pdf' ? <LinkIcon /> : undefined}
                      onDelete={() => handleRemoveFile(index)}
                      deleteIcon={<IconButton size="small"><Delete /></IconButton>}
                      variant="outlined"
                      color={file.type === 'pdf' ? 'primary' : 'default'}
                      sx={{ maxWidth: 200 }}
                    />
                  ))}
                </Box>
              )}
            </Grid>
            
            {/* External Link (alternative) */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Или добавить ссылку на внешний документ
              </Typography>
              <TextField
                fullWidth
                label="Ссылка на документ"
                placeholder="https://..."
                error={!!errors.externalLink}
                helperText={errors.externalLink?.message || 'URL внешнего документа (YouTube, сайт, облачное хранилище)'}
                {...register('externalLink')}
              />
              {externalLink && externalLink.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Ссылка будет добавлена вместо загруженных файлов
                </Typography>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                * Обязательные поля
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading || uploading}>Отмена</Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit(handleFormSubmit)} 
          disabled={loading || uploading}
        >
          {loading || uploading ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}